import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

import { GetDepartments } from "../../../../api/masters/departmentAPI";
import { GetCategoriesMedicalStore } from "../../../../api/medicalStore/master/categoryMedicalStoreAPI";
import { GetItems } from "../../../../api/medicalStore/master/itemAPI";
import { GetIssueReceiveById, UpdateIssueReceive } from "../../../../api/medicalStore/transaction/issueReceiveAPI";
import { GetStockTransferById, UpdateStockTransfer } from "../../../../api/medicalStore/transaction/stockTransferAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  vrType: yup.string().required(),
  vrNo: yup.number().required(),
  vrDate: yup.date().required(),

  fromDepartmentId: yup.string().required(),
  toDepartmentId: yup.string().required(),

  remark: yup.string(),
  netAmount: yup.number().required(),

  productDetails: yup.array().of(
	yup.object().shape({
	  categoryId: yup.string().required(),
	  productId: yup.string().required(),
	  expiryDate: yup.date().required(),
	  batchNo: yup.number().required(),
	  quantity: yup.number().required(),
	  mrp: yup.number().required(),
	  saleRate: yup.number().required(),
	  amount: yup.number().required()
	})
  )
});

const EditStockTransferEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);

  const {
	register,
	control,
	setValue,
	getValues,
	handleSubmit,
	formState: { errors }
  } = useForm({
	resolver: yupResolver(schema),
	defaultValues: {
	  productDetails: [{}]
	}
  });

  const { fields, append, remove } = useFieldArray({
	control,
	name: "productDetails"
  });

 /* ================= CALCULATIONS ================= */
   const calculateRow = index => {
	 const qty = Number(getValues(`productDetails.${index}.quantity`) || 0);
	 const rate = Number(getValues(`productDetails.${index}.saleRate`) || 0);
 
	 const amount = qty * rate;
	 setValue(`productDetails.${index}.amount`, amount.toFixed(2));
   };
 
   const watchedProducts = useWatch({ control, name: "productDetails" });
 
   useEffect(() => {
	 let total = 0;
	 watchedProducts?.forEach(p => {
	   total += Number(p.amount || 0);
	 });
	 setValue("netAmount", total.toFixed(2));
   }, [watchedProducts, setValue]);


  const fetchData = async () => {
    try {
      const response = await GetStockTransferById(id); // Make sure login function returns token
      
      if (response.success) {
        const data = response.data;
        // ================= SIMPLE FIELDS =================
        Object.keys(data).forEach(key => {
          if (key === "productDetails") return;

          // handle date fields if any in future
          if (data[key] instanceof Date || typeof data[key] === "string") {
            if (key.includes("vrDate") || key.includes("billDate") && data[key]) {
              setValue(key, data[key].split("T")[0]);
              return;
            }
          }

          setValue(key, data[key]);
        });

        // ================= MEDICINE DETAILS (ARRAY) =================
        if (Array.isArray(data.productDetails)) {
          data.productDetails.forEach((item, index) => {
            Object.keys(item).forEach(field => {
              if (field === "expiryDate" && item[field]) {
                setValue(
                  `productDetails.${index}.expiryDate`,
                  item[field].split("T")[0]
                );
              } else {
                setValue(
                  `productDetails.${index}.${field}`,
                  item[field]
                );
              }
            });
          });
        }
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  useEffect(() => {
    if (!id) return;
    // Fetch existing data
    const loadAllData = async () => {
      try {
        const [
          departmentRes,
          categoryRes,
          itemRes
        ] = await Promise.all([
          GetDepartments(),
          GetCategoriesMedicalStore(),
          GetItems()
        ]);

        if (departmentRes.success) {
          setDepartments(departmentRes.data);
        } else {
          showToast("error", departmentRes.message);
        }

        if (categoryRes.success) {
          setCategories(categoryRes.data);
        } else {
          showToast("error", categoryRes.message);
        }

        if (itemRes.success) {
          setItems(itemRes.data);
        } else {
          showToast("error", itemRes.message);
        }

        // ✅ CALL AFTER ALL ABOVE ARE DONE
        fetchData();
      } catch (error) {
        console.error("Error loading master data:", error);
      }
    };
    loadAllData();
  }, [id]);


  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await UpdateStockTransfer(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Edit Stock Transfer" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Edit Stock Transfer</h5>
            <Link to={`/medical-store/transaction/stock-transfer-entry/list`}>
              <button className="btn btn-primary">Stock Transfer List</button>
            </Link>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="card-body">

              {/* HEADER */}
              <div className="row">
                <div className="col-4">
                  <label>Voucher Type</label>
                  <input className="form-control" {...register("vrType")} />
				  {errors.vrType && <span className="text-danger">{errors.vrType.message}</span>}
                </div>
                <div className="col-4">
                  <label>Voucher No</label>
                  <input type="number" className="form-control" {...register("vrNo")} />
				  {errors.vrNo && <span className="text-danger">{errors.vrNo.message}</span>}
                </div>
                <div className="col-4">
                  <label>Voucher Date</label>
                  <input type="date" className="form-control" {...register("vrDate")} />
				  {errors.vrDate && <span className="text-danger">{errors.vrDate.message}</span>}
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-4">
                  <label>From Department</label>
                  <select className="form-control" {...register("fromDepartmentId")}>
                    <option value="">Select</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
				  {errors.fromDepartmentId && <span className="text-danger">{errors.fromDepartmentId.message}</span>}
                </div>

                <div className="col-4">
                  <label>To Department</label>
                  <select className="form-control" {...register("toDepartmentId")}>
                    <option value="">Select</option>
                    {departments.map(d => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
				  {errors.toDepartmentId && <span className="text-danger">{errors.toDepartmentId.message}</span>}
                </div>

                <div className="col-4">
                  <label>Remark</label>
                  <textarea className="form-control" {...register("remark")} />
				  {errors.remark && <span className="text-danger">{errors.remark.message}</span>}
                </div>
              </div>

              {/* PRODUCTS */}
              <hr />
              <h6>Product Details</h6>

              {fields.map((item, index) => (
                <div className="row border p-2 mb-2 align-items-end" key={item.id}>
                  <div className="col-2">
                    <label>Category</label>
                    <select className="form-control" {...register(`productDetails.${index}.categoryId`)}>
                      <option value="">Select</option>
                      {categories.map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
					{errors[`productDetails.${index}.categoryId`] && <span className="text-danger">{errors[`productDetails.${index}.categoryId`].message}</span>}
                  </div>

                  <div className="col-2">
                    <label>Product</label>
                    <select className="form-control" {...register(`productDetails.${index}.productId`)}>
                      <option value="">Select</option>
                      {items.map(i => (
                        <option key={i._id} value={i._id}>{i.name}</option>
                      ))}
                    </select>
					{errors[`productDetails.${index}.productId`] && <span className="text-danger">{errors[`productDetails.${index}.productId`].message}</span>}
                  </div>

                  <div className="col-2">
                    <label>Expiry</label>
                    <input type="date" className="form-control" {...register(`productDetails.${index}.expiryDate`)} />
					{errors[`productDetails.${index}.expiryDate`] && <span className="text-danger">{errors[`productDetails.${index}.expiryDate`].message}</span>}
                  </div>

                  <div className="col-2">
                    <label>Batch</label>
                    <input type="number" className="form-control" {...register(`productDetails.${index}.batchNo`)} />
					{errors[`productDetails.${index}.batchNo`] && <span className="text-danger">{errors[`productDetails.${index}.batchNo`].message}</span>}
                  </div>

                  <div className="col-2">
                    <label>Qty</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`productDetails.${index}.quantity`, {
                        onChange: () => calculateRow(index)
                      })}
                    />
					{errors[`productDetails.${index}.quantity`] && <span className="text-danger">{errors[`productDetails.${index}.quantity`].message}</span>}
                  </div>

                  <div className="col-2">
                    <label>MRP</label>
                    <input type="number" className="form-control" {...register(`productDetails.${index}.mrp`)} />
					{errors[`productDetails.${index}.mrp`] && <span className="text-danger">{errors[`productDetails.${index}.mrp`].message}</span>}
                  </div>

                  <div className="col-2">
                    <label>Sale Rate</label>
                    <input
                      type="number"
                      className="form-control"
                      {...register(`productDetails.${index}.saleRate`, {
                        onChange: () => calculateRow(index)
                      })}
                    />
					{errors[`productDetails.${index}.saleRate`] && <span className="text-danger">{errors[`productDetails.${index}.saleRate`].message}</span>}
                  </div>

                  <div className="col-2">
                    <label>Amount</label>
                    <input readOnly className="form-control" {...register(`productDetails.${index}.amount`)} />
					{errors[`productDetails.${index}.amount`] && <span className="text-danger">{errors[`productDetails.${index}.amount`].message}</span>}
                  </div>

                  <div className="col-2 text-center">
                    <button type="button" className="btn btn-danger" onClick={() => remove(index)}>
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              <button type="button" className="btn btn-secondary" onClick={() => append({})}>
                + Add Product
              </button>

              <hr />
              <div className="row">
                <div className="col-3">
                  <label>Net Amount</label>
                  <input className="form-control" {...register("netAmount")} readOnly />
				  {errors.netAmount && <span className="text-danger">{errors.netAmount.message}</span>}
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default EditStockTransferEntry;
